import React, { Fragment, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Link } from "react-router-dom";
import moment from "moment";
import { getUserSubscriptionList } from "./../../actions/subscriptionActions";
import { isSubscriptionCurrentlyActive } from "../../utils/examSubscription";
import CustomDataTable from "../Datatable/CustomDataTable";
import Loader from "../Loader";
import "./UserSubscriptions.css";

const getExamTone = (slug = "", title = "") => {
  const value = `${slug} ${title}`.toLowerCase();
  if (value.includes("jamb")) return "blue";
  if (value.includes("wassce") || value.includes("waec")) return "green";
  if (value.includes("post-utme") || value.includes("post utme")) return "orange";
  if (value.includes("bece") || value.includes("jsce")) return "purple";
  return "blue";
};

const matchesSearchQuery = (query, values = []) => {
  const normalized = query.trim().toLowerCase();
  if (!normalized) return true;
  return values.some((value) => String(value || "").toLowerCase().includes(normalized));
};

/** Duration is stored in days; mock shows plan length in months. */
const getPlanMonths = (subscription) => {
  const durationDays = Number(subscription?.duration);
  if (Number.isFinite(durationDays) && durationDays > 0) {
    return Math.max(1, Math.round(durationDays / 30));
  }

  const planText = `${subscription?.planName || ""} ${subscription?.description || ""}`;
  const monthMatch = planText.match(/(\d+)\s*month/i);
  if (monthMatch) {
    return Number(monthMatch[1]);
  }

  const yearMatch = planText.match(/(\d+)\s*year/i);
  if (yearMatch) {
    return Number(yearMatch[1]) * 12;
  }

  return "—";
};

const getExpiryMeta = (expireAt) => {
  if (!expireAt) {
    return { label: "No expiry", tone: "active", daysLeft: null };
  }

  const expiry = moment(expireAt);
  if (!expiry.isValid()) {
    return { label: "Unknown", tone: "expired", daysLeft: null };
  }

  const daysLeft = expiry.startOf("day").diff(moment().startOf("day"), "days");
  if (daysLeft < 0) {
    return { label: "Expired", tone: "expired", daysLeft };
  }

  return {
    label: `${daysLeft}d left`,
    tone: "active",
    daysLeft,
  };
};

const UserSubscriptions = ({ history }) => {
  const dispatch = useDispatch();
  const { user } = useSelector((state) => state.auth);
  const { userSubscriptionList, sloading } = useSelector((state) => state.examSubscriptions);
  const [searchQuery, setSearchQuery] = useState("");

  useEffect(() => {
    if (user?._id) {
      dispatch(getUserSubscriptionList(user._id, { includeInactive: true }));
    }
  }, [dispatch, user]);

  const subscriptions = Array.isArray(userSubscriptionList) ? userSubscriptionList : [];

  const pageStats = useMemo(() => {
    const activePlans = subscriptions.filter((item) => isSubscriptionCurrentlyActive(item)).length;
    const expiringSoon = subscriptions.filter((item) => {
      if (!isSubscriptionCurrentlyActive(item) || !item?.expireAt) return false;
      const daysLeft = moment(item.expireAt).startOf("day").diff(moment().startOf("day"), "days");
      return daysLeft >= 0 && daysLeft <= 30;
    }).length;

    return {
      total: subscriptions.length,
      activePlans,
      expiringSoon,
    };
  }, [subscriptions]);

  const filteredSubscriptions = useMemo(
    () =>
      subscriptions.filter((item) =>
        matchesSearchQuery(searchQuery, [
          item?.exam?.title,
          item?.planName,
          item?.description,
          item?.university?.title,
        ])
      ),
    [subscriptions, searchQuery]
  );

  const handleUpgrade = (examSlug, examId, examTitle, universityId) => {
    if (examId) {
      localStorage.setItem("examId", examId);
    }
    if (examTitle) {
      localStorage.setItem("examName", examTitle);
    }
    if (examSlug) {
      localStorage.setItem("examSlug", examSlug);
    }

    if (examSlug === "post-utme" && universityId) {
      localStorage.setItem("universityId", universityId);
    } else {
      localStorage.removeItem("universityId");
    }

    localStorage.setItem("forceSubscriptionRenewal", "true");
    history.push("/subscription-plans");
  };

  const renderExamCell = (title, slug, universityTitle) => {
    const tone = getExamTone(slug, title);
    const label = universityTitle ? `${title} (${universityTitle})` : title || "—";

    return (
      <span className="student-subscriptions__exam">
        <span className={`student-subscriptions__exam-dot student-subscriptions__exam-dot--${tone}`} />
        {label}
      </span>
    );
  };

  const tableHeader = [
    { title: "Exam", prop: "exam", sortable: false, filterable: false },
    { title: "Plan (Months)", prop: "plan", sortable: false, filterable: false },
    { title: "Description", prop: "description", sortable: false, filterable: false },
    { title: "Subscribed At", prop: "subscribeAt", sortable: true, filterable: false },
    { title: "Expiration Date", prop: "expirationDate", sortable: false, filterable: false },
    { title: "Action", prop: "action", sortable: false, filterable: false },
  ];

  const tableRowData = filteredSubscriptions.map((item) => {
    const expiryMeta = getExpiryMeta(item?.expireAt);
    const planMonths = getPlanMonths(item);

    return {
      exam: renderExamCell(item?.exam?.title, item?.exam?.slug, item?.university?.title),
      plan: <span className="student-subscriptions__plan">{planMonths}</span>,
      description: (
        <span className="student-subscriptions__description">
          {item?.description || item?.planName || "—"}
        </span>
      ),
      subscribeAt: item?.subscribedAt ? moment(item.subscribedAt).format("YYYY-MM-DD") : "—",
      expirationDate: (
        <div className="student-subscriptions__expiry">
          <span className="student-subscriptions__date">
            {item?.expireAt ? moment(item.expireAt).format("YYYY-MM-DD") : "—"}
          </span>
          <span
            className={`student-subscriptions__status student-subscriptions__status--${expiryMeta.tone}`}
          >
            <i
              className={`fas ${expiryMeta.tone === "expired" ? "fa-times" : "fa-clock"}`}
              aria-hidden="true"
            />
            {expiryMeta.label}
          </span>
        </div>
      ),
      action: (
        <button
          type="button"
          className="student-subscriptions__upgrade"
          onClick={() =>
            handleUpgrade(
              item?.exam?.slug,
              item?.exam?._id,
              item?.exam?.title,
              item?.university?._id
            )
          }
        >
          Upgrade Plan
          <i className="fas fa-external-link-alt" aria-hidden="true" />
        </button>
      ),
    };
  });

  const onSortFunction = {
    subscribeAt(columnValue) {
      if (!columnValue || columnValue === "—") return 0;
      return moment(columnValue, "YYYY-MM-DD").valueOf();
    },
  };

  const customLabels = {
    first: "<<",
    last: ">>",
    prev: "<",
    next: ">",
    show: "Display",
    entries: "rows",
    noResults: "There are no subscriptions to display",
  };

  const resultCount = filteredSubscriptions.length;

  return (
    <Fragment>
      {sloading || !user ? (
        <Loader />
      ) : (
        <div className="student-subscriptions">
          <div className="student-subscriptions__header">
            <h1>Subscriptions</h1>
            <p>Manage your exam subscription plans</p>
          </div>

          <div className="student-subscriptions__stats">
            <div className="student-subscriptions__stat">
              <span className="student-subscriptions__stat-icon student-subscriptions__stat-icon--blue">
                <i className="fas fa-layer-group" aria-hidden="true" />
              </span>
              <div>
                <p className="student-subscriptions__stat-label">Total Subscriptions</p>
                <p className="student-subscriptions__stat-value">{pageStats.total}</p>
                <p className="student-subscriptions__stat-subtext">across all exams</p>
              </div>
            </div>
            <div className="student-subscriptions__stat">
              <span className="student-subscriptions__stat-icon student-subscriptions__stat-icon--green">
                <i className="fas fa-check-circle" aria-hidden="true" />
              </span>
              <div>
                <p className="student-subscriptions__stat-label">Active Plans</p>
                <p className="student-subscriptions__stat-value">{pageStats.activePlans}</p>
                <p className="student-subscriptions__stat-subtext">currently running</p>
              </div>
            </div>
            <div className="student-subscriptions__stat">
              <span className="student-subscriptions__stat-icon student-subscriptions__stat-icon--orange">
                <i className="fas fa-hourglass-half" aria-hidden="true" />
              </span>
              <div>
                <p className="student-subscriptions__stat-label">Expiring Soon</p>
                <p className="student-subscriptions__stat-value">{pageStats.expiringSoon}</p>
                <p className="student-subscriptions__stat-subtext">within 30 days</p>
              </div>
            </div>
          </div>

          <div className="student-subscriptions__table-card">
            <div className="student-subscriptions__table-head">
              <div>
                <h2>My Subscriptions</h2>
                <p>
                  {resultCount} subscription{resultCount === 1 ? "" : "s"} found
                </p>
              </div>
              <div className="student-subscriptions__search-wrap">
                <i className="fas fa-search student-subscriptions__search-icon" aria-hidden="true" />
                <input
                  type="search"
                  className="student-subscriptions__search"
                  placeholder="Search by exam or description..."
                  value={searchQuery}
                  onChange={(event) => setSearchQuery(event.target.value)}
                />
              </div>
            </div>

            {subscriptions.length === 0 ? (
              <div className="student-subscriptions__empty">
                <p>You do not have any subscriptions yet.</p>
                <Link to="/exams" className="student-subscriptions__empty-btn">
                  Browse Exam Plans
                  <i className="fas fa-arrow-right" aria-hidden="true" />
                </Link>
              </div>
            ) : (
              <CustomDataTable
                tableHeaders={tableHeader}
                tableBody={tableRowData}
                rowsPerPage={10}
                rowsPerPageOption={[10, 20, 30, 50]}
                initialSort={{ prop: "subscribeAt", isAscending: false }}
                onSort={onSortFunction}
                labels={customLabels}
              />
            )}
          </div>
        </div>
      )}
    </Fragment>
  );
};

export default UserSubscriptions;
